import { Injectable } from "@angular/core";
import { catchError, map, switchMap } from "rxjs/operators";
import { IDocumentDialogstate, IAttachment } from "../model/dialog-state.model";
import { SharedService } from "./shared.service";
import { SoApiService } from "./soapi.service";
import { UnsafeFileService } from "./unsafe-files.service";
import { defer, forkJoin, from, Observable, of, throwError } from "rxjs";
import { GraphService } from "./graph/graph.service";
import { OutlookService } from "./outlook/outlook.service";
import { AuthService } from "./auth.service";
import { MonitoringService } from "./monitoring.service";
import { SharedMailboxService } from "./shared-mailbox.service";
import { SmimeUnpackService } from "./smime-unpack.service";

@Injectable({
  providedIn: "root",
})
export class AttachmentService {
  public attachmentSelected: IAttachment;

  constructor(
    private readonly so: SoApiService,
    private readonly sharedService: SharedService,
    private readonly outlookService: OutlookService,
    private readonly unsafeFileService: UnsafeFileService,
    private readonly graphService: GraphService,
    private readonly authService: AuthService,
    private monitoringService: MonitoringService,
    private readonly sharedMailboxService: SharedMailboxService,
    private readonly smimeUnpackService: SmimeUnpackService
  ) { }

  getAttachmentsInfo(): Observable<IAttachment[]> {
    const attachmentsReq: Observable<IDocumentDialogstate>[] = [];
    const isOutgoing = this.sharedService.isCurrentEmailOutgoing;
    const attachmentInfo = this.outlookService.attachments.filter(att => !att.isInline);

    for (const att of attachmentInfo) {
      const req = this.so.getAttachmentDialogState(
        this.sharedService.officeMailbox.item.from.emailAddress,
        this.getFileInfo(att.name)[1],
        isOutgoing,
        this.sharedService.officeMailboxInternetMessageId + att.name,
        [null]
      );

      attachmentsReq.push(req);
    }

    const results = attachmentsReq.length > 0 ? forkJoin(attachmentsReq) : of([]);
    return this.setAttachmentObject(attachmentInfo, results).pipe(
      switchMap(attachments =>
        this.expandSmimeAttachments(attachments).pipe(
          catchError(err => {
            this.monitoringService.logCustomEvent("expandSmimeAttachments failed", { err: String(err) });
            return of(attachments);
          })
        )
      )
    );
  }

  private getAttachmentBase64ForUnpack(att: IAttachment): Observable<string> {
    const graphFetch = (): Observable<string> => defer(() => {
      const itemId = this.sharedService.getItemRestId();
      const restId = this.sharedService.getAttachmentRestId(att.OfficeAttachment.id);
      return this.graphService.getAttachment<any>(itemId, restId).pipe(
        switchMap((meta): Observable<string> => {
          if (meta?.contentBytes) return of(meta.contentBytes as string);
          const url = this.buildAttachmentUrl(itemId, restId);
          return from(this.authService.getAccessToken(false)).pipe(
            switchMap(token =>
              from(fetch(`${url}/$value`, { headers: { Authorization: `Bearer ${token}` } }))
            ),
            switchMap(response => {
              if (!response.ok) throw new Error(`Failed to fetch S/MIME attachment binary: ${response.statusText}`);
              return from(response.arrayBuffer());
            }),
            switchMap(arrayBuffer => from(this.graphService.arrayBufferToBase64(arrayBuffer)))
          );
        })
      );
    });

    if (this.outlookService.supportsAttachmentContent) {
      return from(this.outlookService.getAttachmentContent(att.OfficeAttachment.id)).pipe(
        map(c => c.content),
        catchError(() => graphFetch())
      );
    }

    return graphFetch();
  }

  private expandSmimeAttachments(attachments: IAttachment[]): Observable<IAttachment[]> {
    const toUnpack = attachments.filter(
      att => this.isSmimeAttachment(att.OfficeAttachment) && att.OfficeAttachment.id
    );

    if (toUnpack.length === 0) {
      // Classic Outlook exposes inner files from signed emails with id=null but omits the .p7m itself.
      // Fetch the full email EML from Graph to extract and serve the inner attachments.
      if (attachments.some(a => !a.OfficeAttachment.id)) {
        return this.expandSmimeFromEml(attachments);
      }
      return of(attachments);
    }

    const isOutgoing = this.sharedService.isCurrentEmailOutgoing;

    const unpackJobs = toUnpack.map(att =>
      this.getAttachmentBase64ForUnpack(att).pipe(
        switchMap(base64 => from(this.smimeUnpackService.unpack(base64))),
        switchMap(extracted => {
          if (extracted.length === 0) return of([] as IAttachment[]);

          const dialogRequests = extracted.map(file =>
            this.so.getAttachmentDialogState(
              this.sharedService.officeMailbox.item.from.emailAddress,
              this.getFileInfo(file.name)[1],
              isOutgoing,
              this.sharedService.officeMailboxInternetMessageId + file.name,
              [null]
            )
          );

          return forkJoin(dialogRequests).pipe(
            map(dialogStates => extracted.map((file, i) => ({
              IsSafe: this.unsafeFileService.isSafe(file.name),
              IsDigitallySigned: false,
              OfficeAttachment: {
                id: null,
                name: file.name,
                contentType: file.contentType,
                size: 0,
                attachmentType: "file",
                isInline: false,
              } as Office.AttachmentDetails,
              DialogState: dialogStates[i] as IDocumentDialogstate,
              extractedContent: file,
            } as IAttachment))
          ));
        }),
        catchError(err => {
          this.monitoringService.logCustomEvent("Failed to unpack S/MIME attachment", { err: String(err) });
          return of(null); // null signals failure — keep original
        })
      )
    );

    return forkJoin(unpackJobs).pipe(
      map(unpackedArrays => {
        const result: IAttachment[] = [];
        toUnpack.forEach((original, i) => {
          const unpacked = unpackedArrays[i];
          if (unpacked === null) {
            // unpack failed — keep original blocked entry
            result.push(original);
          } else {
            result.push(...unpacked);
          }
        });
        const smimeNames = new Set(toUnpack.map(a => a.OfficeAttachment.name));
        // Exclude no-id entries: OL Classic exposes inner envelope files (e.g. emojis.txt)
        // as separate attachments with id=null. These are covered by the unpack result.
        const remaining = attachments.filter(a => !smimeNames.has(a.OfficeAttachment.name) && !!a.OfficeAttachment.id);
        return [...remaining, ...result];
      })
    );
  }

  private expandSmimeFromEml(attachments: IAttachment[]): Observable<IAttachment[]> {
    const itemId = this.sharedService.getItemRestId();
    const isOutgoing = this.sharedService.isCurrentEmailOutgoing;

    return this.graphService.getMessageEml(itemId).pipe(
      switchMap(eml => from(this.smimeUnpackService.unpackEml(eml))),
      switchMap(extracted => {
        const p7m = extracted.find(f => f.name.toLowerCase().endsWith(".p7m"));
        if (p7m) {
          // pkcs7-mime format: PostalMime returned the outer envelope as an attachment.
          // Run the CMS unpack pass to reach the inner files.
          return from(this.smimeUnpackService.unpack(p7m.content)).pipe(
            map(inner => inner.filter(f => !this.isSmimeAttachment({ name: f.name } as Office.AttachmentDetails)))
          );
        }
        return of(extracted.filter(f => !this.isSmimeAttachment({ name: f.name } as Office.AttachmentDetails)));
      }),
      switchMap(realFiles => {
        if (realFiles.length === 0) return of(attachments);

        const dialogRequests = realFiles.map(file =>
          this.so.getAttachmentDialogState(
            this.sharedService.officeMailbox.item.from.emailAddress,
            this.getFileInfo(file.name)[1],
            isOutgoing,
            this.sharedService.officeMailboxInternetMessageId + file.name,
            [null]
          )
        );

        return forkJoin(dialogRequests).pipe(
          map(dialogStates => {
            const remaining = attachments.filter(a => !!a.OfficeAttachment.id);
            const extracted: IAttachment[] = realFiles.map((file, i) => ({
              IsSafe: this.unsafeFileService.isSafe(file.name),
              IsDigitallySigned: false,
              OfficeAttachment: {
                id: null,
                name: file.name,
                contentType: file.contentType,
                size: 0,
                attachmentType: "file",
                isInline: false,
              } as Office.AttachmentDetails,
              DialogState: dialogStates[i] as IDocumentDialogstate,
              extractedContent: file,
            }));
            return [...remaining, ...extracted];
          })
        );
      }),
      catchError(err => {
        this.monitoringService.logCustomEvent("expandSmimeFromEml failed", { err: String(err) });
        return of(attachments);
      })
    );
  }

  private setAttachmentObject(
    mailAttachments: Office.AttachmentDetails[],
    documents: Observable<IDocumentDialogstate[]>
  ) {
    return documents.pipe(map(documents => {
      const attachments = [] as IAttachment[];
      for (let index = 0; index < mailAttachments.length; index++) {
        attachments.push({
          IsSafe: this.unsafeFileService.isSafe(mailAttachments[index].name),
          IsDigitallySigned: this.isSmimeAttachment(mailAttachments[index]) || !mailAttachments[index].id,
          OfficeAttachment: mailAttachments[index] as Office.AttachmentDetails,
          DialogState: documents[index] as IDocumentDialogstate,
        });
      }
      return attachments;
    }));
  }

  public isSmimeAttachment(attachment: Office.AttachmentDetails): boolean {
    const name = (attachment.name ?? "").toLowerCase();
    return name.endsWith(".p7s") || name.endsWith(".p7m");
  }

  /**
   * Get attachment in base64 format from mailbox
   *
   * @param attachmentId Outlook mailbox retruned id for attachment
   */
  getAttachment(attachmentId: string): Observable<{ content: string, contentType: string, name: string }> {
    if (this.attachmentSelected?.extractedContent) {
      return of(this.attachmentSelected.extractedContent);
    }

    if (this.outlookService.supportsAttachmentContent) {
      return from(this.outlookService.getAttachmentContent(attachmentId)).pipe(
        map(attachmentContent => {
          if (attachmentContent.format !== Office.MailboxEnums.AttachmentContentFormat.Base64) {
            throw new Error(`Unexpected attachment format: ${attachmentContent.format}`);
          }
          return {
            content: attachmentContent.content,
            contentType: this.attachmentSelected?.OfficeAttachment?.contentType ?? "application/octet-stream",
            name: this.attachmentSelected?.OfficeAttachment?.name ?? "attachment",
          };
        }),
        catchError(error => {
          this.monitoringService.logCustomEvent("Failed to fetch attachment via getAttachmentContentAsync", { error });
          return throwError(() => error);
        })
      );
    }

    const itemId = this.sharedService.getItemRestId();
    const attachmentRestId = this.sharedService.getAttachmentRestId(attachmentId);
    const url = this.buildAttachmentUrl(itemId, attachmentRestId);

    return this.graphService.getAttachment<any>(itemId, attachmentRestId).pipe(
      switchMap(metadata => {
        if (metadata?.contentBytes) {
          this.monitoringService.logCustomEvent("Small attachment");
          // ✅ Small attachment → already Base64
          return of({
            content: metadata.contentBytes,
            contentType: metadata.contentType || "application/octet-stream",
            name: metadata.name
          });
        }

        // 2. Large attachment → fetch binary stream, convert to Base64
        // TODO refactor to use GraphService method for fetching large attachments. Story: #90154
        return from(this.authService.getAccessToken(false)).pipe(
          switchMap(token =>
            from(fetch(`${url}/$value`, {
              headers: { Authorization: `Bearer ${token}` }
            }))
          ),
          switchMap(response => {
            if (!response.ok) {
              return throwError(() => new Error(`Failed to fetch attachment: ${response.statusText}`));
            }
            this.monitoringService.logCustomEvent("Large attachment. Use ArrayBuffer");
            return from(response.arrayBuffer());
          }),
          switchMap(arrayBuffer =>
            from(this.graphService.arrayBufferToBase64(arrayBuffer)).pipe(
              map(content => ({
                content,
                contentType: metadata?.contentType || "application/octet-stream",
                name: metadata?.name || "attachment"
              }))
            )
          )
        );
      }),
      catchError(error => {
        this.monitoringService.logCustomEvent("Failed to fetch attachment", { error });
        return throwError(() => error);
      })
    );
  }

  /**
 * Constructs the Graph API URL for accessing an attachment.
 * Ensures both itemId and attachmentId are encoded.
 */
  buildAttachmentUrl(itemId: string, attachmentId: string): string {
    const encodedItemId = encodeURIComponent(itemId);
    const encodedAttachmentId = encodeURIComponent(encodeURIComponent(attachmentId));
    return `https://graph.microsoft.com/v1.0/${this.sharedMailboxService.mailboxSegment}/messages/${encodedItemId}/attachments/${encodedAttachmentId}`;
  }

  getFileInfo(filename: string) {
    const re = /(?:\.([^.]+))?$/;
    const ext = re.exec(filename)[0];
    const name = filename.substring(0, filename.length - ext.length);

    return [name, ext];
  }
}
